import React, { useState } from "react";
import {
  StyleSheet,
  View,
  TouchableOpacity,
  ScrollView,
  Image,
  Platform,
  type StyleProp,
  type ViewStyle,
} from "react-native";
import { router } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import { ThemedText } from "../ThemedText";
import { ThemedToolTip } from "../ThemedToolTip";
import { ThemedChip } from "../ThemedChip";
import { ThemedPlatformSymbol } from "../ThemedPlatformSymbol";
import { ThemedCompactCard } from "./ThemedCompactCard";
import { ThemedKebabMenu, type IKebabActionItem } from "../ThemedKebabMenu";
import { useThemeColor } from "@/hooks/useThemeColor";
import { useComponentTheme } from "@/hooks/useComponentTheme";
import { ButtonPaddings } from "@/constants/themes/components";
import { CardEntityType, formatCardTimestamp } from "common247";
import { navigateToCardEntity } from "./card-helpers";

export interface IThemedFullCardProps {
  /** Entity domain type */
  entityType: CardEntityType;
  /** Primary identifier */
  id?: string;
  /** Primary title or headline */
  title: string;
  /** Date string */
  date?: string;
  /** Formatted date display string */
  formattedDate?: string;
  /** Creation timestamp */
  createdAt?: number;
  /** Full description or body narrative */
  description?: string;
  /** Image thumbnail or hero image URL */
  thumbnailUrl?: string;
  /** Email address for contacts */
  email?: string;
  /** Author / Owner display name */
  ownerDisplayName?: string;
  /** Whether the user is the owner */
  isOwner?: boolean;
  /** Whether the item is the logged-on AppUser / self-contact */
  isSelf?: boolean;
  /** Whether the entity has a 5-star rating */
  isFiveStar?: boolean;
  /** User role ('owner' | 'editor' | 'viewer') */
  userRole?: string;
  /** Contextual kebab menu actions */
  kebabActions?: IKebabActionItem[];
  /** Associated media items */
  mediaList?: any[];
  /** Associated linked contacts */
  contactsList?: any[];
  /** Associated linked groups */
  groupsList?: any[];
  /** Associated linked memories */
  memoriesList?: any[];
  /** Associated linked places */
  placesList?: any[];
  /** Associated linked anchors */
  anchorsList?: any[];
  /** Associated tags */
  tagsList?: any[];
  /** Associated shares */
  sharesList?: any[];
  onPress?: () => void;
  onEditPress?: () => void;
  onDeletePress?: () => void;
  onSharePress?: () => void;
  style?: StyleProp<ViewStyle>;
  testID?: string;
}

/**
 * ThemedFullCard: Tier 4 Item Card component (Option A: Partitioned Ledger Folio).
 *
 * Implements comprehensive multi-line display with distinct partitioned sections:
 * - Top provenance header with full title, timestamps, and Platform Symbols
 * - Full formatted description/narrative
 * - Partitioned trays:
 *   - Media Highlights: `<ThemedCompactCard entityType="media">`
 *   - Linked Entities (Contacts, Groups, Memories, Places, Anchors): `<ThemedChip>`
 *   - Tags & Provenance: Tangerine `<ThemedChip entityType="tag">`
 *   - Sharing & Access: Heliotrope `<ThemedChip entityType="share">`
 */
export function ThemedFullCard({
  entityType,
  id,
  title,
  date,
  formattedDate,
  createdAt,
  description,
  thumbnailUrl,
  email,
  ownerDisplayName,
  isOwner = false,
  isSelf = false,
  isFiveStar = false,
  userRole = "owner",
  kebabActions = [],
  mediaList = [],
  contactsList = [],
  groupsList = [],
  memoriesList = [],
  placesList = [],
  anchorsList = [],
  tagsList = [],
  sharesList = [],
  onPress,
  onEditPress,
  onDeletePress,
  onSharePress,
  style,
  testID,
}: IThemedFullCardProps): React.JSX.Element {
  const [isHovered, setIsHovered] = useState(false);

  const { card: cardTheme } = useComponentTheme();
  const cardBg = cardTheme.chrome.backgroundColor;
  const borderColor = cardTheme.chrome.restingBorderColor;
  const tintColor = cardTheme.chrome.selectedBorderColor;
  const textColor = cardTheme.chrome.textColor;
  const textMuted = cardTheme.chrome.textMutedColor;
  const badgeBg = cardTheme.chrome.badgeBgColor;

  const displayDate = formattedDate || date;

  // Build effective kebab actions if not passed explicitly but callback props exist
  const effectiveKebabActions: IKebabActionItem[] = [...kebabActions];
  if (effectiveKebabActions.length === 0) {
    if (onSharePress) {
      effectiveKebabActions.push({
        id: "share",
        label: "Share",
        icon: "share-social-outline",
        onPress: onSharePress,
      });
    }
    if (onEditPress) {
      effectiveKebabActions.push({
        id: "edit",
        label: "Edit",
        icon: "pencil-outline",
        onPress: onEditPress,
      });
    }
    if (onDeletePress) {
      effectiveKebabActions.push({
        id: "delete",
        label: "Delete",
        icon: "trash-outline",
        destructive: true,
        onPress: onDeletePress,
      });
    }
  }

  const handleCardPress = () => {
    if (onPress) {
      onPress();
      return;
    }
    navigateToCardEntity(entityType, id, "ThemedFullCard");
  };

  return (
    <TouchableOpacity
      testID={testID}
      activeOpacity={0.9}
      onPress={handleCardPress}
      {...(Platform.OS === "web"
        ? ({
            onMouseEnter: () => setIsHovered(true),
            onMouseLeave: () => setIsHovered(false),
          } as any)
        : {})}
      style={[
        styles.ledgerCard,
        {
          backgroundColor: cardBg,
          borderColor: isHovered ? tintColor : borderColor,
        },
        style,
      ]}
      accessibilityRole={Platform.select({ web: "article" as any, default: undefined })}
      accessibilityLabel={`Full card: ${title}`}
    >
      {/* 1. Header Provenance Tray */}
      <View style={[styles.headerTray, { borderBottomColor: borderColor }]}>
        <View style={styles.headerLeftWithAvatar}>
          {thumbnailUrl && entityType !== "media" ? (
            <Image source={{ uri: thumbnailUrl }} style={styles.headerAvatarThumbnail} />
          ) : null}
          <View style={styles.headerLeft}>
            <ThemedText type="stamp" style={[styles.entityStamp, { color: tintColor }]}>
              {entityType.toUpperCase()} FOLIO {id ? `// #${id.slice(-6)}` : ""}
            </ThemedText>
            <ThemedText type="editorialTitle" style={styles.titleText}>
              {title}
            </ThemedText>
            {email ? (
              <ThemedText style={[styles.dateText, { color: textMuted }]}>
                ✉ {email}
              </ThemedText>
            ) : null}
            {displayDate && (!createdAt || (displayDate !== formatCardTimestamp(createdAt) && displayDate !== `Created ${formatCardTimestamp(createdAt)}` && displayDate !== `Created: ${formatCardTimestamp(createdAt)}`)) ? (
              <ThemedText style={[styles.dateText, { color: textMuted }]}>
                📅 {displayDate} {ownerDisplayName ? `• Provenance: ${ownerDisplayName}` : ""}
              </ThemedText>
            ) : null}
            {createdAt ? (
              <ThemedText style={[styles.dateText, { color: textMuted }]}>
                🕒 Created: {formatCardTimestamp(createdAt)}
              </ThemedText>
            ) : null}
          </View>
        </View>

        {/* Platform Symbols & Actions */}
        <View style={styles.symbolsTray}>
          {isSelf && (
            <ThemedPlatformSymbol type="you" size="small" showLabel />
          )}
          {isOwner && (
            <ThemedPlatformSymbol type="owner" size="small" showLabel />
          )}
          {isFiveStar && (
            <ThemedPlatformSymbol type="fivestar" size="small" showLabel />
          )}
          {effectiveKebabActions.length > 0 && (
            <ThemedKebabMenu
              actions={effectiveKebabActions}
              compact={true}
              accessibilityLabel={`Options for ${title}`}
            />
          )}
        </View>
      </View>

      {/* 1.5 Media Hero Preview (if media entity or hero preview provided) */}
      {thumbnailUrl && entityType === "media" ? (
        <View style={[styles.mediaHeroContainer, { backgroundColor: badgeBg }]}>
          <Image
            source={{ uri: thumbnailUrl }}
            style={styles.mediaHeroImage}
            resizeMode="contain"
          />
        </View>
      ) : null}

      {/* 2. Narrative Body */}
      {description ? (
        <View style={styles.narrativeSection}>
          <ThemedText style={[styles.narrativeText, { color: textColor }]}>
            {description}
          </ThemedText>
        </View>
      ) : null}

      {/* 3. Partitioned Tray: Links (unified contacts, groups, memories) */}
      <View style={[styles.trayContainer, { borderTopColor: borderColor }]}>
        <View style={styles.trayHeaderRow}>
          <Ionicons name="link-outline" size={12} color={tintColor} />
          <ThemedText type="stamp" style={[styles.trayTitle, { color: textMuted }]}>
            LINKS ({(Array.isArray(contactsList) ? contactsList : Object.values(contactsList || {})).length + (Array.isArray(groupsList) ? groupsList : Object.values(groupsList || {})).length + (Array.isArray(memoriesList) ? memoriesList : Object.values(memoriesList || {})).length + (Array.isArray(placesList) ? placesList : Object.values(placesList || {})).length + (Array.isArray(anchorsList) ? anchorsList : Object.values(anchorsList || {})).length})
          </ThemedText>
        </View>
        {(Array.isArray(contactsList) ? contactsList : Object.values(contactsList || {})).length > 0 || (Array.isArray(groupsList) ? groupsList : Object.values(groupsList || {})).length > 0 || (Array.isArray(memoriesList) ? memoriesList : Object.values(memoriesList || {})).length > 0 || (Array.isArray(placesList) ? placesList : Object.values(placesList || {})).length > 0 || (Array.isArray(anchorsList) ? anchorsList : Object.values(anchorsList || {})).length > 0 ? (
          <View style={styles.chipsWrap}>
            {(Array.isArray(contactsList) ? contactsList : Object.values(contactsList || {})).map((c: any, idx: number) => {
              const item = typeof c === "string" ? { id: c, displayName: c } : c;
              const contactId = item.id || item.contactId;
              return (
                <ThemedChip
                  key={`full-contact-${contactId || idx}`}
                  entityType="contact"
                  id={contactId}
                  label={item.displayName || item.name || "Contact"}
                  thumbnailUrl={item.photoIconUrl || item.photoUrl}
                  isOwner={item.isOwner}
                />
              );
            })}
            {(Array.isArray(groupsList) ? groupsList : Object.values(groupsList || {})).map((g: any, idx: number) => {
              const item = typeof g === "string" ? { id: g, name: g } : g;
              const groupId = item.id || item.groupId;
              return (
                <ThemedChip
                  key={`full-group-${groupId || idx}`}
                  entityType="group"
                  id={groupId}
                  label={item.name || item.displayName || item.title || "Group"}
                />
              );
            })}
            {(Array.isArray(memoriesList) ? memoriesList : Object.values(memoriesList || {})).map((m: any, idx: number) => {
              const item = typeof m === "string" ? { id: m, title: "Memory" } : m;
              const memoryId = item.id || item.memoryId;
              return (
                <ThemedChip
                  key={`full-memory-${memoryId || idx}`}
                  entityType="memory"
                  id={memoryId}
                  label={item.label || item.title || item.name || (item as any)?.memoryTitle || "Memory"}
                  isFiveStar={item.favouriteRating === 5}
                />
              );
            })}
            {(Array.isArray(placesList) ? placesList : Object.values(placesList || {})).map((p: any, idx: number) => {
              const item = typeof p === "string" ? { id: p, name: "Place" } : p;
              const placeId = item.id || item.placeId;
              return (
                <ThemedChip
                  key={`full-place-${placeId || idx}`}
                  entityType="place"
                  id={placeId}
                  label={item.name || item.displayName || "Place"}
                />
              );
            })}
            {(Array.isArray(anchorsList) ? anchorsList : Object.values(anchorsList || {})).map((a: any, idx: number) => {
              const item = typeof a === "string" ? { id: a, name: "Anchor" } : a;
              const anchorId = item.id || item.anchorId;
              return (
                <ThemedChip
                  key={`full-anchor-${anchorId || idx}`}
                  entityType="anchor"
                  id={anchorId}
                  label={item.name || item.displayName || "Anchor"}
                />
              );
            })}
          </View>
        ) : (
          <ThemedText style={[styles.emptyTrayText, { color: textMuted }]}>
            No links
          </ThemedText>
        )}
      </View>

      {/* 4. Partitioned Tray: Media Highlights (Suppressed on media cards as the media asset is the primary content) */}
      {entityType !== "media" && (
        <View style={[styles.trayContainer, { borderTopColor: borderColor }]}>
          <View style={styles.trayHeaderRow}>
            <Ionicons name="images-outline" size={12} color={tintColor} />
            <ThemedText type="stamp" style={[styles.trayTitle, { color: textMuted }]}>
              MEDIA HIGHLIGHTS ({mediaList.length})
            </ThemedText>
          </View>
          {mediaList.length > 0 ? (
            <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.mediaRow}>
              {mediaList.map((item: any, idx) => (
                <ThemedCompactCard
                  key={`full-media-${item.id || item.mediaId || idx}`}
                  entityType="media"
                  id={item.id || item.mediaId}
                  title={item.title || item.name}
                  thumbnailUrl={item.downloadUrl || item.thumbnailUrl || item.url}
                  isOwner={item.isOwner}
                  isFiveStar={item.isFiveStar || item.favouriteRating === 5}
                />
              ))}
            </ScrollView>
          ) : (
            <ThemedText style={[styles.emptyTrayText, { color: textMuted }]}>
              No media highlights attached
            </ThemedText>
          )}
        </View>
      )}

      {/* 6. Partitioned Tray: Tags & Classification */}
      {(() => {
        const normalizedTags = Array.isArray(tagsList) ? tagsList : Object.values(tagsList || {});
        return (
          <View style={[styles.trayContainer, { borderTopColor: borderColor }]}>
            <View style={styles.trayHeaderRow}>
              <Ionicons name="pricetags-outline" size={12} color="#F28500" />
              <ThemedText type="stamp" style={[styles.trayTitle, { color: textMuted }]}>
                TAGS &amp; CLASSIFICATION ({normalizedTags.length})
              </ThemedText>
            </View>
            {normalizedTags.length > 0 ? (
              <View style={styles.chipsWrap}>
                {normalizedTags.map((t: any, idx: number) => (
                  <ThemedChip
                    key={`full-tag-${t.id || t.tagId || idx}`}
                    entityType="tag"
                    tagType={t.tagType}
                    id={t.id || t.tagId}
                    label={t.value || t.name || t.label || "Tag"}
                  />
                ))}
              </View>
            ) : (
              <ThemedText style={[styles.emptyTrayText, { color: textMuted }]}>
                No tags assigned
              </ThemedText>
            )}
          </View>
        );
      })()}

      {/* 7. Partitioned Tray: Sharing & Access Control */}
      <View style={[styles.trayContainer, { borderTopColor: borderColor }]}>
        <View style={styles.trayHeaderRow}>
          <Ionicons name="paper-plane-outline" size={12} color="#C885B4" />
          <ThemedText type="stamp" style={[styles.trayTitle, { color: textMuted }]}>
            SHARING &amp; ACCESS ({sharesList.length})
          </ThemedText>
        </View>
        {sharesList.length > 0 ? (
          <View style={styles.chipsWrap}>
            {sharesList.map((s, idx) => (
              <ThemedChip
                key={`full-share-${s.id || idx}`}
                entityType="share"
                id={s.id}
                label={s.recipientDisplayName || s.recipientName || s.targetDisplayName || s.targetTitle || title}
                secondaryLabel={s.recipientDisplayName ? (s.targetTitle || title) : s.recipientName}
              />
            ))}
          </View>
        ) : (
          <ThemedText style={[styles.emptyTrayText, { color: textMuted }]}>
            Not shared
          </ThemedText>
        )}
      </View>
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  ledgerCard: {
    borderWidth: 2,
    borderRadius: 4,
    padding: 10,
    gap: 6,
    overflow: "visible",
  },
  headerTray: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    paddingBottom: 6,
    borderBottomWidth: 1,
    flexWrap: "wrap",
    gap: 6,
    overflow: "visible",
  },
  headerLeftWithAvatar: {
    flex: 1,
    minWidth: 220,
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 10,
  },
  headerAvatarThumbnail: {
    width: 38,
    height: 38,
    borderRadius: 4,
    marginTop: 2,
  },
  headerLeft: {
    flex: 1,
    minWidth: 180,
    gap: 2,
  },
  entityStamp: {
    fontSize: 9,
    letterSpacing: 1,
  },
  titleText: {
    fontSize: 16,
    lineHeight: 22,
  },
  dateText: {
    fontSize: 11,
    marginTop: 2,
  },
  symbolsTray: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    zIndex: 10,
    overflow: "visible",
  },
  symbolBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: ButtonPaddings.action.paddingHorizontal,
    paddingVertical: ButtonPaddings.action.paddingVertical,
    borderRadius: 3,
  },
  symbolText: {
    fontSize: 10,
    fontWeight: "700",
  },
  narrativeSection: {
    paddingVertical: 0,
  },
  narrativeText: {
    fontSize: 13,
    lineHeight: 19,
  },
  trayContainer: {
    borderTopWidth: 1,
    paddingTop: 4,
    gap: 3,
  },
  trayHeaderRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    marginBottom: 0,
  },
  trayTitle: {
    fontSize: 10,
    letterSpacing: 0.8,
  },
  mediaHeroContainer: {
    width: "100%",
    minHeight: 180,
    maxHeight: 340,
    borderRadius: 4,
    overflow: "hidden",
    alignItems: "center",
    justifyContent: "center",
    marginVertical: 4,
  },
  mediaHeroImage: {
    width: "100%",
    height: 280,
  },
  mediaRow: {
    flexDirection: "row",
    gap: 6,
    paddingVertical: 0,
  },
  chipsWrap: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 4,
  },
  emptyTrayText: {
    fontSize: 11,
    fontStyle: "italic",
    paddingVertical: 0,
    paddingHorizontal: 0,
  },
});

export default ThemedFullCard;
